import { Injectable, isDevMode } from '@angular/core';
import { LoggedInUserKeys, LoggedInUserService } from './logged-in-user.service';
import { AppUsersDto } from '../model/app-users-dto';
import {
  PERMISSION_CATALOGUE,
  PermissionBlock,
  PermissionEntry,
  PermissionGroupEntry,
} from './permission.catalogue';

// Everything below is derived from PERMISSION_CATALOGUE — add or change a permission there,
// never here.

// ---------- the catalogue, flattened ----------

type CatalogueEntry<B> = B extends { actions: readonly (infer A)[] }
  ? B | A
  : B extends { standalone: readonly (infer S)[] }
    ? S
    : never;
type AnyEntry = CatalogueEntry<(typeof PERMISSION_CATALOGUE)[number]['blocks'][number]>;

const isGroup = (block: PermissionBlock): block is PermissionGroupEntry => 'actions' in block;

/** Every entry with the group it sits under; an action inherits its group's `requires`. */
const ENTRIES: { entry: PermissionEntry; group?: PermissionGroupEntry }[] =
  PERMISSION_CATALOGUE.flatMap((section) =>
    section.blocks.flatMap((block: PermissionBlock) =>
      isGroup(block)
        ? [{ entry: block }, ...block.actions.map((entry) => ({ entry, group: block }))]
        : block.standalone.map((entry) => ({ entry })),
    ),
  );

/** Every name by key, for code and templates: components expose it as `readonly P = Perm`
 *  and call `_permissionServices.can(P.EDIT_DRIVE)` instead of typing the string. */
export const Perm = Object.fromEntries(
  ENTRIES.map(({ entry }) => [entry.key, entry.name]),
) as { readonly [E in AnyEntry as E['key']]: E['name'] };

export type PermissionKey = (typeof Perm)[keyof typeof Perm];

const KNOWN_PERMISSIONS = new Set<string>(Object.values(Perm));

/** Which group each action belongs to — the relationship `can()` resolves against. */
export const ACTION_GROUP: Record<string, string> = Object.fromEntries(
  ENTRIES.filter(({ group }) => group).map(({ entry, group }) => [entry.name, group.name]),
);

/** The group names, so the grid can mark them as covering the rest of their block. */
export const GROUP_PERMISSIONS: string[] = [...new Set(Object.values(ACTION_GROUP))];

/** How the Add/Edit User grid lays the catalogue out: the group first, then what it covers. */
export const PERMISSION_LAYOUT: { label: string; permissions: string[] }[] =
  PERMISSION_CATALOGUE.map((section) => ({
    label: section.label,
    permissions: section.blocks.flatMap((block: PermissionBlock) =>
      isGroup(block)
        ? [block.name, ...block.actions.map((entry) => entry.name)]
        : block.standalone.map((entry) => entry.name),
    ),
  }));

/** Section anything unmapped falls into, so a new catalogue name is never unassignable. */
export const PERMISSION_SECTION_FALLBACK = 'Other';

/** What a new Client HR starts with. */
export const CLIENT_HR_DEFAULT_PERMISSIONS: string[] = ENTRIES.filter(
  ({ entry }) => entry.clientHrDefault,
).map(({ entry }) => entry.name);

/** Login-token code → name. Codes the FE does not know are dropped on decode. */
const NAME_BY_CODE: Record<string, string> = Object.fromEntries(
  ENTRIES.filter(({ entry }) => entry.code).map(({ entry }) => [entry.code, entry.name]),
);

// ---------- dependencies ----------

const nameOfKey = (key: string): string => {
  const name = (Perm as Record<string, string>)[key];
  if (!name) {
    // a typo in a `requires` list — fail on load rather than quietly never matching
    throw new Error(`[permission.catalogue] unknown key in requires: ${key}`);
  }
  return name;
};

/**
 * Name → what it is useless without, as names (see `Requirement`). Read by the Add/Edit User
 * grid and by the route guards that sit behind a single action; `can()` does not read it — a
 * name still answers for itself alone.
 */
export const PERMISSION_REQUIRES: Record<string, string[][]> = Object.fromEntries(
  ENTRIES.filter(({ entry, group }) => entry.requires ?? group?.requires).map(
    ({ entry, group }) => [
      entry.name,
      (entry.requires ?? group.requires).map((anyOf) => anyOf.map(nameOfKey)),
    ],
  ),
);

/**
 * The requirement lists of `name` that `held` does not meet. A group in `held` counts for
 * its actions, as in `can()`. With `catalogue`, a name the backend has not created yet is
 * dropped from every list, and a list left empty is skipped — nobody could grant it anyway.
 */
export function unmetRequirements(
  name: string,
  held: ReadonlySet<string>,
  catalogue?: ReadonlySet<string>,
): string[][] {
  const holds = (n: string) => held.has(n) || held.has(ACTION_GROUP[n]);
  return (PERMISSION_REQUIRES[name] || [])
    .map((anyOf) => (catalogue ? anyOf.filter((n) => catalogue.has(n)) : anyOf))
    .filter((anyOf) => anyOf.length && !anyOf.some(holds));
}

/** Held names that are missing something they need. */
export function strandedPermissions(
  held: ReadonlySet<string>,
  catalogue?: ReadonlySet<string>,
): Set<string> {
  return new Set([...held].filter((name) => unmetRequirements(name, held, catalogue).length));
}

/** Held name → what it is missing, as text: "Drives Tab or SR Tab and ...". */
export function requirementWarnings(
  held: ReadonlySet<string>,
  catalogue?: ReadonlySet<string>,
): Record<string, string> {
  const warnings: Record<string, string> = {};
  for (const name of held) {
    const unmet = unmetRequirements(name, held, catalogue);
    if (unmet.length) {
      warnings[name] = unmet.map((anyOf) => anyOf.join(' or ')).join(' and ');
    }
  }
  return warnings;
}

/**
 * Adds to `held` the first choice of every requirement `names` miss, and of what that adds,
 * until nothing is missing. Returns what it added.
 */
export function addRequirements(
  names: Iterable<string>,
  held: Set<string>,
  catalogue: ReadonlySet<string>,
): string[] {
  const added: string[] = [];
  let pending = [...names];
  while (pending.length) {
    pending = pending.flatMap((name) =>
      unmetRequirements(name, held, catalogue).map(([first]) => first),
    );
    pending = [...new Set(pending)].filter((name) => !held.has(name));
    pending.forEach((name) => held.add(name));
    added.push(...pending);
  }
  return added;
}

/**
 * Removes from `held` what the last change left without a requirement, repeating until
 * nothing else falls. Names in `strandedBefore` were already stranded (an old user saved
 * before this check) and are left alone — the warning and the save check point at those.
 * Returns what it removed.
 */
export function dropNewlyStranded(
  held: Set<string>,
  strandedBefore: ReadonlySet<string>,
  catalogue: ReadonlySet<string>,
): string[] {
  const removed: string[] = [];
  const newlyStranded = () =>
    [...strandedPermissions(held, catalogue)].filter((name) => !strandedBefore.has(name));
  for (let fallen = newlyStranded(); fallen.length; fallen = newlyStranded()) {
    fallen.forEach((name) => held.delete(name));
    removed.push(...fallen);
  }
  return removed;
}

// ---------- tab tags ----------

/** The sidebar-tab names: the catalogue's first section. */
const TAB_PERMISSIONS: string[] = PERMISSION_LAYOUT[0].permissions;

/**
 * Name → the tag the Add/Edit User grid shows beside a permission that works on more than one
 * tab, naming those tabs ("SR | Drives"). Read off `requires`; single-tab names get none.
 */
export const PERMISSION_TAB_TAG: Record<string, { label: string; title: string }> = Object.fromEntries(
  Object.entries(PERMISSION_REQUIRES)
    .map(([name, lists]) => [name, TAB_PERMISSIONS.filter((tab) => lists.some((anyOf) => anyOf.includes(tab)))] as const)
    .filter(([, tabs]) => tabs.length > 1)
    .map(([name, tabs]) => [
      name,
      {
        label: tabs.map((tab) => tab.replace(/ Tab$/, '')).join(' | '),
        title: `Works on ${tabs.join(', ')}`,
      },
    ]),
);

// ---------- runtime ----------

/** `permissionToken` = 'SVFJR0FJ' (base64 of the tenant marker) + base64 of comma-joined codes. */
const PERMISSION_DATA_PREFIX = 'SVFJR0FJ';

function decodePermissions(token: string): string[] {
  if (!token || !token.startsWith(PERMISSION_DATA_PREFIX)) return [];
  try {
    const text = atob(token.slice(PERMISSION_DATA_PREFIX.length));
    return text ? text.split(',').map((code) => code.trim()).filter(Boolean) : [];
  } catch {
    // malformed base64 — treat as no permissions rather than breaking every gated view
    return [];
  }
}

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  constructor(private readonly _loggedInUserService: LoggedInUserService) {}

  private getUser(): AppUsersDto {
    return this._loggedInUserService.getLoggedInUser(LoggedInUserKeys.LOGGED_IN_USER);
  }

  /** `can()` runs per row per change-detection pass, and reading the user means a full
   *  `JSON.parse`. Keyed on the raw string so a re-login still invalidates it. */
  private cachedRaw: string | null = null;
  private cachedNames = new Set<string>();

  private getPermissionNames(): Set<string> {
    const raw =
      typeof localStorage === 'undefined'
        ? null
        : localStorage.getItem(LoggedInUserKeys.LOGGED_IN_USER);
    if (raw === this.cachedRaw) {
      return this.cachedNames;
    }

    const user = this.getUser();
    this.cachedRaw = raw;
    // permissionToken is the current login contract; permissions[] only for sessions stored before it
    this.cachedNames = new Set(
      user?.permissionToken
        ? decodePermissions(user.permissionToken)
            .map((code) => NAME_BY_CODE[code])
            .filter(Boolean)
        : Array.isArray(user?.permissions)
          ? user.permissions.map((p) => p.name)
          : [],
    );
    return this.cachedNames;
  }

  /**
   * The check every gated action uses. Access is granted by the action itself **or** by the
   * group it sits under, so an admin can tick one group or pick individual actions, and the
   * templates do not care which.
   *
   * Denying a single action out of an otherwise granted group therefore means granting the
   * actions instead of the group — the group always wins.
   */
  can(action: PermissionKey): boolean {
    // Templates are not strictly type-checked, so a typo like `P.EDT_DRIVE` arrives here as
    // undefined and would just hide the button. Say so in dev instead of failing silently.
    if (isDevMode() && !KNOWN_PERMISSIONS.has(action)) {
      console.warn(`[PermissionService] unknown permission: ${String(action)}`);
    }
    const granted = this.getPermissionNames();
    return granted.has(action) || granted.has(ACTION_GROUP[action]);
  }

  /** Returns true if the logged-in user has at least one of the given actions or groups. */
  canAny(actions: readonly PermissionKey[]): boolean {
    return actions.some((action) => this.can(action));
  }
}
